/**
 * Savings strike on the add-to-cart button — CD canvas frame 2h, "Savings on the button (9)".
 *
 * Renders the running total of what the button will actually add, and, when there is a real
 * markdown, the struck was-price beside it:
 *
 *   Ajouter au panier — 89€                        no markdown, nothing struck, nothing promised
 *   Ajouter au panier — 109€ 89€                   the chino carries a compare-at
 *   Ajouter les 3 articles — 156€ 129€             chino + belt marked down, socks full price
 *
 * WHY A SIBLING NODE, NOT THE LABEL TEXT
 * Two writers already own this button's label — the theme's own re-render (product-info.js resets
 * it to «Ajouter au panier» at the end of every variant change) and cross-sell-block.js (the
 * «Choisir les tailles» gate and the plural «Ajouter les N articles»). Both write
 * `span.textContent`, and both have already produced a race on this ticket. So this module writes
 * NOTHING they write: it owns one appended sibling span and replaces it wholesale. The label span
 * stays the first <span> in the button, so `button.querySelector('span')` still resolves to it for
 * everyone else.
 *
 * WHAT THE FIGURES ARE
 * Real prices only. The struck figure sums compare-at prices; where a line has no compare-at it
 * contributes its own price, so the strike is a floor. Automatic cart-level promotions are
 * EXCLUDED unless a measured mode prices them — checkout can then only beat the strike, never
 * undercut it. Exception (operator ruling 08/10): the Pack Chino dégressif prices the viewed chino
 * at its position in the basket, chinos already in the cart included, from the same Liquid snippets
 * as the price under the title, so the button never disagrees with the price beside it. For the same reason the label makes no «−X €» / «économisez» claim: a bare was/now
 * pair is a floor, not a promise. Cart-level offers stay in the cross-sell block's heading and
 * badges, as today.
 *
 * The cross-sell block's own `cs_discounted_price` is NOT used: it is a display figure derived
 * from the cross_sell metafield with no discount code behind it (see the memory note
 * `cross-sell-block-price-mechanism`), so spending it here would promise a total the cart never
 * charges.
 */
(function () {
  'use strict';

  var TOTAL_CLASS = 'product-form__submit-total';

  /**
   * Money, in the store's own format. Mirrors `money_without_trailing_zeros`: whole amounts drop
   * the decimals (89€, not 89,00€), which is what the price block beside the button renders.
   * Reuses the theme's own formatter rather than introducing a second money implementation.
   */
  /**
   * The no-decimals placeholders theme.Currency.formatMoney actually implements. Swapping to one
   * OUTSIDE this set is not a cosmetic miss — formatMoney's switch has no branch for it, so it
   * returns the format with an EMPTY amount and the button reads «CHF » with no number. The
   * Swiss market's apostrophe format is exactly that case, so the set is a whitelist, not a
   * regex: an unlisted format keeps its decimals rather than losing its figure.
   */
  var NO_DECIMAL_FORMS = {
    amount: 'amount_no_decimals',
    amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
    amount_no_decimals: 'amount_no_decimals',
    amount_no_decimals_with_comma_separator: 'amount_no_decimals_with_comma_separator',
    amount_no_decimals_with_space_separator: 'amount_no_decimals_with_space_separator'
  };

  function formatMoney(cents) {
    var settings = (window.theme && theme.shopSettings) || {};
    var format = settings.moneyFormat;
    if (!format || !(window.theme && theme.Currency)) return null;

    if (!settings.currencyCode && cents % 100 === 0) {
      format = format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, placeholder) {
        var swapped = NO_DECIMAL_FORMS[placeholder];
        return swapped ? '{{' + swapped + '}}' : match;
      });
    }

    var money = theme.Currency.formatMoney(cents, format);
    // A format the theme's switch does not implement yields an amount-less string. Better no
    // total on the button than a currency symbol with no figure beside it.
    return /\d/.test(money) ? money : null;
  }

  function toCents(value) {
    var n = parseInt(value, 10);
    return isNaN(n) || n < 0 ? 0 : n;
  }

  /**
   * The block a card belongs to decides whether — and how — its automatic offer may be priced.
   * Both modes were verified against real carts before being offered as settings; the setting
   * defaults to 'none' so an unverified offer is never priced.
   */
  function blockRule(card) {
    var block = card.closest('cross-sell-block');
    return {
      block: block,
      mode: (block && block.dataset.discountMode) || 'none',
      amount: block ? toCents(block.dataset.discountAmount) : 0,
      // Is the product being VIEWED itself eligible for this block's offer? Liquid answers it from
      // collection membership, not from a per-product amount — that amount belongs to a different
      // offer and can be cleared while eligibility stands.
      mainEligible: !!(block && block.dataset.mainEligible === 'true'),
      // Pack Chino dégressif ('tiered'): the 2nd eligible item gets `second`, the 3rd and beyond
      // get `next`. Both are presentment-currency cents resolved in Liquid from the shop metafields.
      second: block ? toCents(block.dataset.tierSecond) : 0,
      next: block ? toCents(block.dataset.tierNext) : 0
    };
  }

  /** Amount off the eligible item at 1-based position `position` under the dégressif pack. */
  function tierAmount(position, second, next) {
    if (position <= 1) return 0;
    return position === 2 ? second : next;
  }

  /**
   * Pack Chino dégressif — the cards of a 'tiered' block show the price of the chino they would
   * be. Counting starts after the chinos already in the cart and the viewed chino (when eligible):
   * ticked cards take the next positions in page order, and every unticked card shows the price
   * of the next chino. So with an empty cart on a chino page, the first ticked card reads -10 and
   * the rest -25. Display only: this never decides what the button charges (see render()).
   *
   * Reads the block's own data (Liquid), not data-discount-mode: the card prices are shown on every
   * market (converted amounts), while the button total stays gated to the shop currency.
   */
  function relabelTieredCards(root) {
    (root || document).querySelectorAll('cross-sell-block').forEach(function (block) {
      var second = toCents(block.dataset.tierSecond);
      var next = toCents(block.dataset.tierNext);
      if (!second && !next) return;

      var position = toCents(block.dataset.packCart) + (block.dataset.mainEligible === 'true' ? 1 : 0);

      block.querySelectorAll('.cross-sell-block__card').forEach(function (card) {
        var inner = card.querySelector('[data-cs-ref-price]');
        if (!inner) return;
        var reference = toCents(inner.dataset.csRefPrice);
        var checkbox = card.querySelector('.cross-sell-block__checkbox');
        var amount;
        if (checkbox && checkbox.checked) {
          position += 1;
          amount = tierAmount(position, second, next);
        } else {
          amount = tierAmount(position + 1, second, next);
        }
        if (!amount || amount >= reference) return;

        var sale = card.querySelector('.price-item--sale bdi');
        var saleText = formatMoney(Math.round((reference - amount) / 100) * 100);
        if (sale && saleText && sale.textContent !== saleText) sale.textContent = saleText;

        var badge = card.querySelector('.cs-badge');
        if (badge) {
          var badgeText = /%/.test(badge.textContent)
            ? '-' + Math.round((amount * 100) / reference) + '%'
            : (formatMoney(amount) ? '-' + formatMoney(amount) : null);
          if (badgeText && badge.textContent !== badgeText) badge.textContent = badgeText;
        }
      });
    });
  }

  /** The price pair a cross-sell line contributes, or null when it is not really going in. */
  function crossSellLine(card) {
    var checkbox = card.querySelector('.cross-sell-block__checkbox');
    var input = card.querySelector('[data-cross-sell-variant-select]');
    if (!checkbox || !checkbox.checked || !input || !input.value) return null;

    // Single-variant card: the price rides on the hidden input, there is no strip to read.
    if (input.dataset.price !== undefined) {
      return {
        price: toCents(input.dataset.price),
        compareAt: toCents(input.dataset.compareAt),
        discount: toCents(input.dataset.discount),
        rule: blockRule(card)
      };
    }

    // Sized card. The chosen size's markup lives EITHER in the shared strip (when this is the card
    // currently open) or still inside the card's own <template>. Template content is a fragment,
    // so it is unreachable from document.querySelector — go through `.content` explicitly.
    var selector = '[data-cross-sell-size-option][data-variant-id="' + input.value + '"]';
    var block = card.closest('cross-sell-block') || document;
    var option = block.querySelector(selector);
    if (!option) {
      var template = card.querySelector('template[data-cross-sell-sizes]');
      if (template && template.content) option = template.content.querySelector(selector);
    }
    if (!option) return null;

    return {
      price: toCents(option.dataset.price),
      compareAt: toCents(option.dataset.compareAt),
      discount: toCents(option.dataset.discount),
      rule: blockRule(card)
    };
  }

  /**
   * Is the button currently asking for something rather than offering to add? While it is, there
   * is no settled basket to price, so the total stays off.
   */
  function isGated(button) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return true;
    if (button.dataset.crossSellGate === 'true') return true;

    var productInfo = button.closest('product-info');
    return !!(productInfo && productInfo.hasAttribute('data-selection-pending'));
  }

  function render(button) {
    var existing = button.querySelector('.' + TOTAL_CLASS);
    if (existing) existing.remove();

    if (isGated(button)) return;

    var price = toCents(button.dataset.lpPrice);
    if (!price) return;
    var compareAt = toCents(button.dataset.lpCompareAt);

    // LePantalon's PDP ships no quantity selector today, so this is always 1. It is read anyway:
    // the selector is a block setting a merchant can switch on, and the failure it would cause is
    // silent and expensive — the button would price one item while adding several.
    var quantityInput = button.form && button.form.querySelector('input[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    if (!quantity || quantity < 1) quantity = 1;

    var total = price * quantity;
    var struck = (compareAt > price ? compareAt : price) * quantity;

    // Pack Chino dégressif — the VIEWED chino is priced at its position in the basket, exactly as
    // the price under the title prices it (same Liquid snippets, seeded on the button): with one
    // chino already in the cart it is the 2nd (-second), with two or more it is a 3rd+ (-next),
    // with none it is full price. Operator ruling 08/10: the button must agree with the price
    // under the title, the collection cards and the cross-sell cards, so chinos ALREADY in the cart
    // are counted here, and the cross-sell positions below continue from this one.
    var packPosition = 0;
    var packMainCounted = false;
    var packMainOff = 0;
    var packSecond = toCents(button.dataset.lpPackSecond);
    var packNext = toCents(button.dataset.lpPackNext);
    if (button.dataset.lpPackEligible === 'true') {
      packPosition = toCents(button.dataset.lpPackCart);
      if (packSecond > 0 || packNext > 0) {
        for (var unit = 0; unit < quantity; unit += 1) {
          packPosition += 1;
          packMainOff += Math.min(tierAmount(packPosition, packSecond, packNext), price);
        }
        packMainCounted = true;
      }
    }

    // "Each additional item" counts ELIGIBLE items. Eligibility is block membership, resolved in
    // Liquid — NOT the presence of a per-product amount, which belongs to another offer and can be
    // cleared without changing eligibility. The main product is counted when it belongs to the same
    // collection: on a chino page the chino IS the first chino, and missing it would discount one
    // item too many and make the button undercut the till.
    var eligibleCount = 0;
    var mainCounted = false;
    var flatAmount = 0;
    var perProductOff = 0;
    var tierSecond = 0;
    var tierNext = 0;
    var tieredCards = 0;
    var tieredStart = -1;

    var productInfo = button.closest('product-info') || document;
    productInfo.querySelectorAll('.cross-sell-block__card').forEach(function (card) {
      var line = crossSellLine(card);
      if (!line || !line.price) return;
      total += line.price;
      struck += line.compareAt > line.price ? line.compareAt : line.price;

      if (line.rule.mode === 'per_product') {
        // Each item's own amount, unconditional — nothing depends on the rest of the basket.
        perProductOff += Math.min(line.discount, line.price);
      } else if (line.rule.mode === 'tiered') {
        // Positions continue after the chinos already in the cart and the viewed chino (below).
        tieredCards += 1;
        tierSecond = line.rule.second;
        tierNext = line.rule.next;
        if (tieredStart < 0) {
          tieredStart = packMainCounted
            ? packPosition
            : toCents(line.rule.block && line.rule.block.dataset.packCart) + (line.rule.mainEligible ? 1 : 0);
        }
      } else if (line.rule.mode === 'each_additional') {
        eligibleCount += 1;
        flatAmount = line.rule.amount;
        // Count the viewed product once, and only for a block it is actually eligible under.
        if (line.rule.mainEligible && !mainCounted) {
          eligibleCount += 1;
          mainCounted = true;
        }
      }
    });

    // Items ALREADY in the basket are deliberately not counted. They can only ever increase the
    // real discount, so ignoring them keeps the button on the safe side of the till.
    if (eligibleCount > 1 && flatAmount > 0) {
      perProductOff += (eligibleCount - 1) * flatAmount;
    }
    // Pack Chino dégressif: 2nd = second, 3rd and beyond = next, counted from the chinos already in
    // the cart and the viewed chino — the same positions the cards display (relabelTieredCards).
    if (tieredCards > 0 && (tierSecond > 0 || tierNext > 0)) {
      for (var offset = 1; offset <= tieredCards; offset += 1) {
        perProductOff += tierAmount(tieredStart + offset, tierSecond, tierNext);
      }
    }

    total = Math.max(0, total - perProductOff - packMainOff);

    var now = formatMoney(total);
    if (!now) return;

    var wrapper = document.createElement('span');
    wrapper.className = TOTAL_CLASS;
    wrapper.appendChild(document.createTextNode(' — '));

    // Strike only on a REAL markdown. Equal totals mean nothing was marked down, and a struck
    // figure identical to the price would read as a claim where there is none.
    if (struck > total) {
      var was = document.createElement('s');
      was.className = TOTAL_CLASS + '-was';
      was.textContent = formatMoney(struck);
      wrapper.appendChild(was);
      wrapper.appendChild(document.createTextNode(' '));
    }

    var nowNode = document.createElement('span');
    nowNode.className = TOTAL_CLASS + '-now';
    nowNode.textContent = now;
    wrapper.appendChild(nowNode);

    button.appendChild(wrapper);
  }

  function buttons() {
    return document.querySelectorAll('[data-lp-total-button]');
  }

  function refresh() {
    relabelTieredCards(document);
    buttons().forEach(render);
  }

  /**
   * The main product's price follows the selected variant. The theme's variantChange payload
   * carries the variant object, and it publishes at the END of its re-render — the same hook
   * cross-sell-block.js settled on, for the same reason: anything earlier gets overwritten.
   */
  function bindVariantChange() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
      var variant = payload && payload.data && payload.data.variant;
      var sectionId = payload && payload.data && payload.data.sectionId;

      buttons().forEach(function (button) {
        var productInfo = button.closest('product-info');
        // Ignore a re-render belonging to another product section on the same page. Mirrors
        // product-info.js's own `sectionId` getter — a quick-view renders with an
        // `originalSection`, so reading `data-section` alone would mismatch and drop the update.
        var mine = productInfo && (productInfo.dataset.originalSection || productInfo.dataset.section);
        if (sectionId && mine && mine !== sectionId) return;
        if (variant) {
          button.dataset.lpPrice = variant.price || 0;
          button.dataset.lpCompareAt = variant.compare_at_price || 0;
        }
        // The re-rendered section carries a fresh pack seed (the cart may have changed since load,
        // e.g. a chino added from this page): take it, so the button keeps agreeing with the
        // re-rendered price under the title.
        var html = payload && payload.data && payload.data.html;
        var fresh = html && html.querySelector && html.querySelector('[data-lp-total-button]');
        if (fresh) {
          ['lpPackEligible', 'lpPackCart', 'lpPackSecond', 'lpPackNext'].forEach(function (key) {
            if (fresh.dataset[key] !== undefined) button.dataset[key] = fresh.dataset[key];
          });
        }
        render(button);
      });
    });
  }

  window.LPButtonTotal = { refresh: refresh };

  bindVariantChange();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
